import apiClient from "@/api/apiClient";
import type {
  GlobalReportListItem,
  GlobalReportListParams,
  KycReport,
  ReportCreatorOption,
} from "@/types/kyc";

export type BatchListMode = 'person' | 'company_cnpj8' | 'company_cnpj14' | 'mixed';

export interface EntityReportDetails {
  report_status: string;
  risk_level: string | null;
  total_flags: number;
  flags_medium: number;
  flags_high: number;
  flags_very_high: number;
  person_entities_analyzed: number;
  company_entities_analyzed: number;
  category_risks: Record<string, BatchRiskCategorySummary>;
}

export interface BatchReportOverview {
  total_reports: number;
  person_entities_analyzed: number;
  company_entities_analyzed: number;
  risk_distribution: Record<string, number>;
}

export interface BatchRiskCategorySummary {
  category_name: string | null;
  category_instance_ids: string[];
  level_text: string;
  level_value: number | null;
  entities_checked: number;
  entities_failed: number;
  checks_performed: number;
  checks_failed: number;
  checks_low: number;
  checks_medium: number;
  checks_high: number;
  checks_very_high: number;
  has_missing_data: boolean;
  has_outdated_data: boolean;
}

export interface BatchRiskSummaryItem {
  request_id: string;
  primary_entity_tax_id_full: string;
  primary_entity_tax_id_basic: string;
  entity_name: string | null;
  total_risk: string;
  medium_risk_count: number;
  high_risk_count: number;
  very_high_risk_count: number;
  person_entities_analyzed: number;
  company_entities_analyzed: number;
  category_risks: Record<string, BatchRiskCategorySummary>;
}

export interface BatchRiskSummaryResponse {
  items: BatchRiskSummaryItem[];
  total: number;
}

export interface ReportCategoryOverviewItem {
  category_slug: string;
  category_name: string | null;
  category_instance_ids: string[];
  entities_checked: number;
  entities_failed: number;
  checks_performed: number;
  checks_failed: number;
  checks_low: number;
  checks_medium: number;
  checks_high: number;
  checks_very_high: number;
  reports_with_missing_data: number;
  reports_with_outdated_data: number;
}

export interface ReportCategoryOverviewResponse {
  items: ReportCategoryOverviewItem[];
  total: number;
}

export interface GlobalReportTimelinePoint {
  date: string;
  count: number;
}

export interface GlobalReportCreatorSeries {
  key: string;
  label: string;
  count: number;
  timeline: GlobalReportTimelinePoint[];
}

export interface GlobalReportDashboardSummary {
  total_reports: number;
  reports_with_parecer: number;
  risk_distribution: Record<'low' | 'medium' | 'high' | 'very_high', number>;
  timeline: GlobalReportTimelinePoint[];
  creator_series: GlobalReportCreatorSeries[];
  creator_total_count: number;
  unidentified_reports_count: number;
  category_overview: ReportCategoryOverviewItem[];
}

export interface GlobalReportListSummaryItem extends GlobalReportListItem {
  person_entities_analyzed?: number | null;
  company_entities_analyzed?: number | null;
  category_risks?: Record<string, BatchRiskCategorySummary> | null;
}

export interface GlobalReportListResponse {
  items: GlobalReportListSummaryItem[];
  total: number;
  page: number;
  page_size: number;
  dashboard_summary?: GlobalReportDashboardSummary | null;
}

export interface BatchErrorLog {
  error_code: string | null;
  error_message: string | null;
  error_message_user: string | null;
}

export interface BatchItemStatus {
  request_id: string;
  entity_tax_id: string;
  entity_name: string | null;
  entity_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_log: BatchErrorLog | null;
  report_details: EntityReportDetails | null;
}

export interface BatchResponse {
  batch_id: string;
  status: 'draft' | 'pending' | 'processing' | 'completed' | 'partially_completed' | 'failed';
  total_items: number;
  completed_items: number;
  failed_items: number;
  inserted_at: string;
  list_mode: string | null;
  file_name: string | null;
  inserted_by_user_name: string | null;
  person_risk_profile_name: string | null;
  person_risk_profile_version: number | null;
  company_risk_profile_name: string | null;
  company_risk_profile_version: number | null;
  error_log: BatchErrorLog | null;
}

export interface BatchListItem {
  batch_id: string;
  status: BatchResponse['status'];
  total_items: number;
  completed_items: number;
  failed_items: number;
  inserted_at: string;
  list_mode: string | null;
  file_name: string | null;
  inserted_by_user_id: string | null;
  inserted_by_user_name: string | null;
  person_risk_profile_name: string | null;
  person_risk_profile_version: number | null;
  company_risk_profile_name: string | null;
  company_risk_profile_version: number | null;
  report_overview: BatchReportOverview | null;
}

export interface BatchListPage {
  items: BatchListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface BatchItemsPage {
  items: BatchItemStatus[];
  total: number;
  page: number;
  page_size: number;
  report_overview: BatchReportOverview | null;
}

export interface BatchUploadSummary {
  batch_id: string;
  total: number;
  valid_count: number;
  invalid_count: number;
  person_count: number;
  company_count: number;
}

export interface BatchEntityResponse {
  id: string;
  line_number: number;
  raw_line: string;
  identifier: string;
  normalized: string;
  entity_type: 'person' | 'company' | null;
  display_name: string | null;
  valid: boolean;
  reason: string | null;
  source: 'file' | 'manual';
  report_details: EntityReportDetails | null;
}

export interface BatchEntitiesPage {
  items: BatchEntityResponse[];
  total: number;
  page: number;
  page_size: number;
  valid_count: number;
  invalid_count: number;
  report_overview: BatchReportOverview | null;
}

export interface ConfirmBatchRequest {
  person_risk_profile_id?: string | null;
  company_risk_profile_id?: string | null;
  use_policy_decision?: boolean | null;
  relationship_type_ids?: string[] | null;
}

export interface UpdateBatchEntityRequest {
  identifier: string;
}

export interface KycApis {
  getKycReportByRequestId(requestId: string): Promise<KycReport>;
  getRiskProfilesWithReports(entityType: string, entityId: string): Promise<any[]>;
  getKycReportStatus(requestId: string): Promise<any>;
  requestKycReport(entityType: string, entityId: string, profileId: string, options?: { forceRegenerate?: boolean; usePolicyDecision?: boolean; relationshipTypeIds?: string[] }): Promise<any>;
  getGlobalReports(params: GlobalReportListParams & {
    include_row_summary?: boolean;
    include_dashboard_summary?: boolean;
  }): Promise<GlobalReportListResponse>;
  getReportsRiskSummary(requestIds: string[]): Promise<BatchRiskSummaryResponse>;
  getReportCategoryOverview(requestIds: string[]): Promise<ReportCategoryOverviewResponse>;
  getReportCreators(): Promise<ReportCreatorOption[]>;
  listBatches(params: { page: number; page_size: number; status?: string }): Promise<BatchListPage>;
  uploadBatchFile(file: File, listMode: BatchListMode): Promise<BatchUploadSummary>;
  addBatchText(text: string, listMode: BatchListMode, batchId?: string): Promise<BatchUploadSummary>;
  getBatchEntities(batchId: string, params: { valid?: boolean; page: number; page_size: number }): Promise<BatchEntitiesPage>;
  updateBatchEntity(batchId: string, entityId: string, identifier: string): Promise<BatchUploadSummary>;
  deleteBatchEntity(batchId: string, entityId: string): Promise<BatchUploadSummary>;
  deleteInvalidBatchEntities(batchId: string): Promise<BatchUploadSummary>;
  confirmBatch(batchId: string, personRiskProfileId?: string | null, companyRiskProfileId?: string | null, options?: { usePolicyDecision?: boolean; relationshipTypeIds?: string[] }): Promise<BatchResponse>;
  cancelBatch(batchId: string): Promise<void>;
  getBatchStatus(batchId: string): Promise<BatchResponse>;
  getBatchItems(batchId: string, params: { page: number; page_size: number; item_status?: string; search?: string }): Promise<BatchItemsPage>;
  getBatchRiskSummary(batchId: string): Promise<BatchRiskSummaryResponse>;
}


const kycApi: KycApis = {
  getKycReportByRequestId: async (requestId: string): Promise<KycReport> => {
    return await apiClient.get<KycReport>(`/kyc/report/${requestId}`);
  },

  /**
   * Request a new KYC report generation (async).
   * Returns immediately with a request_id - use getKycReportStatus to poll for completion.
   */
  requestKycReport: async (
    entityType: string,
    entityId: string,
    profileId: string,
    options?: { forceRegenerate?: boolean; usePolicyDecision?: boolean; relationshipTypeIds?: string[] }
  ): Promise<{
    request_id: string;
    entity_tax_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    report_data?: any;
  }> => {
    // Auto-decision inputs go in the body (the relationship ids are a list); profile and
    // regenerate stay as query params. Body is null when no auto-decision choice was made.
    const body = options?.usePolicyDecision !== undefined
      ? {
          use_policy_decision: options.usePolicyDecision,
          relationship_type_ids: options.relationshipTypeIds ?? null,
        }
      : null;
    return await apiClient.post(`/kyc/${entityType}/${entityId}/request-report`, body, {
      params: {
        profile_id: profileId,
        force_regenerate: options?.forceRegenerate ?? false,
      },
    });
  },

  /**
   * Poll for KYC report generation status.
   * Returns the status and report_data when completed.
   */
  getKycReportStatus: async (requestId: string): Promise<{
    request_id: string;
    entity_tax_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    report_data?: any;
  }> => {
    return await apiClient.get(`/kyc/report-status/${requestId}`);
  },

  getRiskProfilesWithReports: async (entityType: string, entityId: string): Promise<any[]> => {
    return await apiClient.get(`/kyc/${entityType}/${entityId}/risk-profiles-with-reports`);
  },

  /**
   * Get paginated list of all KYC reports for the account.
   * Supports server-side pagination and filtering.
   */
  getGlobalReports: async (params: GlobalReportListParams & {
    include_row_summary?: boolean;
    include_dashboard_summary?: boolean;
  }): Promise<GlobalReportListResponse> => {
    const queryParams: Record<string, string | number | boolean> = {
      page: params.page,
      page_size: params.page_size,
    };

    if (params.user_id) queryParams.user_id = params.user_id;
    if (params.risk_profile_id) queryParams.risk_profile_id = params.risk_profile_id;
    if (params.entity_type) queryParams.entity_type = params.entity_type;
    if (params.status) queryParams.status = params.status;
    if (params.parecer_status) queryParams.parecer_status = params.parecer_status;
    if (params.parecer_decision) queryParams.parecer_decision = params.parecer_decision;
    if (params.search) queryParams.search = params.search;
    if (params.date_from) queryParams.date_from = params.date_from;
    if (params.date_to) queryParams.date_to = params.date_to;
    if (params.include_row_summary) queryParams.include_row_summary = params.include_row_summary;
    if (params.include_dashboard_summary) queryParams.include_dashboard_summary = params.include_dashboard_summary;

    return await apiClient.get<GlobalReportListResponse>('/kyc/reports', { params: queryParams });
  },

  getReportsRiskSummary: async (requestIds: string[]): Promise<BatchRiskSummaryResponse> => {
    return await apiClient.get<BatchRiskSummaryResponse>('/kyc/reports/risk-summary', {
      params: { request_ids: requestIds },
    });
  },

  getReportCategoryOverview: async (requestIds: string[]): Promise<ReportCategoryOverviewResponse> => {
    return await apiClient.get<ReportCategoryOverviewResponse>('/kyc/reports/category-overview', {
      params: { request_ids: requestIds },
    });
  },

  getReportCreators: async (): Promise<ReportCreatorOption[]> => {
    return await apiClient.get<ReportCreatorOption[]>('/kyc/reports/creators');
  },

  /**
   * List all KYC batches for the account with pagination.
   */
  listBatches: async (params: { page: number; page_size: number; status?: string }): Promise<BatchListPage> => {
    const queryParams: Record<string, string | number> = {
      page: params.page,
      page_size: params.page_size,
    };
    if (params.status) queryParams.status = params.status;
    return await apiClient.get<BatchListPage>('/kyc/report-batch', { params: queryParams });
  },

  uploadBatchFile: async (file: File, listMode: BatchListMode): Promise<BatchUploadSummary> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('list_mode', listMode);
    return await apiClient.post<BatchUploadSummary>('/kyc/report-batch/upload-file', formData);
  },

  addBatchText: async (text: string, listMode: BatchListMode, batchId?: string): Promise<BatchUploadSummary> => {
    return await apiClient.post<BatchUploadSummary>('/kyc/report-batch/add-text', {
      text,
      list_mode: listMode,
      batch_id: batchId ?? null,
    });
  },

  getBatchEntities: async (
    batchId: string,
    params: { valid?: boolean; page: number; page_size: number },
  ): Promise<BatchEntitiesPage> => {
    const queryParams: Record<string, string | number | boolean> = {
      page: params.page,
      page_size: params.page_size,
    };
    if (params.valid !== undefined) {
      queryParams.valid = params.valid;
    }
    return await apiClient.get<BatchEntitiesPage>(`/kyc/report-batch/${batchId}/entities`, {
      params: queryParams,
    });
  },

  updateBatchEntity: async (batchId: string, entityId: string, identifier: string): Promise<BatchUploadSummary> => {
    const payload: UpdateBatchEntityRequest = { identifier };
    return await apiClient.patch<BatchUploadSummary>(`/kyc/report-batch/${batchId}/entities/${entityId}`, payload);
  },

  deleteBatchEntity: async (batchId: string, entityId: string): Promise<BatchUploadSummary> => {
    return await apiClient.delete<BatchUploadSummary>(`/kyc/report-batch/${batchId}/entities/${entityId}`);
  },

  deleteInvalidBatchEntities: async (batchId: string): Promise<BatchUploadSummary> => {
    return await apiClient.delete<BatchUploadSummary>(`/kyc/report-batch/${batchId}/invalid-entities`);
  },

  confirmBatch: async (
    batchId: string,
    personRiskProfileId?: string | null,
    companyRiskProfileId?: string | null,
    options?: { usePolicyDecision?: boolean; relationshipTypeIds?: string[] },
  ): Promise<BatchResponse> => {
    const payload: ConfirmBatchRequest = {
      person_risk_profile_id: personRiskProfileId ?? null,
      company_risk_profile_id: companyRiskProfileId ?? null,
      use_policy_decision: options?.usePolicyDecision ?? null,
      relationship_type_ids: options?.relationshipTypeIds ?? null,
    };
    return await apiClient.post<BatchResponse>(`/kyc/report-batch/${batchId}/confirm`, payload);
  },

  cancelBatch: async (batchId: string): Promise<void> => {
    await apiClient.delete(`/kyc/report-batch/${batchId}`);
  },

  getBatchStatus: async (batchId: string): Promise<BatchResponse> => {
    return await apiClient.get<BatchResponse>(`/kyc/report-batch/${batchId}`);
  },

  getBatchItems: async (
    batchId: string,
    params: { page: number; page_size: number; item_status?: string; search?: string },
  ): Promise<BatchItemsPage> => {
    const queryParams: Record<string, string | number> = {
      page: params.page,
      page_size: params.page_size,
    };
    if (params.item_status) queryParams.item_status = params.item_status;
    if (params.search) queryParams.search = params.search;
    return await apiClient.get<BatchItemsPage>(`/kyc/report-batch/${batchId}/items`, {
      params: queryParams,
    });
  },

  getBatchRiskSummary: async (batchId: string): Promise<BatchRiskSummaryResponse> => {
    return await apiClient.get<BatchRiskSummaryResponse>(`/kyc/report-batch/${batchId}/risk-summary`);
  },
};

export default kycApi;
